import { useMutation } from "@tanstack/react-query"
import type {
  UseMutationOptions,
  UseMutationResult,
} from "@tanstack/react-query"

import client from "~/backend/axiosClient"

import type {
  AnonymizeUserMutationResponse,
  AnonymizeUserPathParams,
} from "../types/AnonymizeUser"

type AnonymizeUserClient = typeof client<
  AnonymizeUserMutationResponse,
  Error,
  never
>
type AnonymizeUser = {
  data: AnonymizeUserMutationResponse
  error: Error
  request: never
  pathParams: AnonymizeUserPathParams
  queryParams: never
  headerParams: never
  response: Awaited<ReturnType<AnonymizeUserClient>>
  client: {
    parameters: Partial<Parameters<AnonymizeUserClient>[0]>
    return: Awaited<ReturnType<AnonymizeUserClient>>
  }
}
/**
 * @summary Anonymize user
 * @link /v1/user/:id/anonymize
 */
export function useAnonymizeUser(
  id: AnonymizeUserPathParams["id"],
  options: {
    mutation?: UseMutationOptions<
      AnonymizeUser["response"],
      AnonymizeUser["error"],
      AnonymizeUser["request"]
    >
    client?: AnonymizeUser["client"]["parameters"]
  } = {}
): UseMutationResult<
  AnonymizeUser["response"],
  AnonymizeUser["error"],
  AnonymizeUser["request"]
> {
  const { mutation: mutationOptions, client: clientOptions = {} } =
    options ?? {}
  return useMutation<AnonymizeUser["response"], AnonymizeUser["error"], never>({
    mutationFn: async () => {
      const res = await client<
        AnonymizeUser["data"],
        AnonymizeUser["error"],
        AnonymizeUser["request"]
      >({
        method: "put",
        url: `/v1/user/${id}/anonymize`,
        ...clientOptions,
      })
      return res
    },
    ...mutationOptions,
  })
}
